
import React, { useState, useMemo, useEffect } from 'react';
import { useAppointments } from './hooks/useAppointments';
import { AppointmentList } from './appointments/AppointmentList';
import { RefundRequestModal } from '@/components/dashboard/RefundRequestModal';
import { Button } from '@/components/ui/button';
import { RefreshCcw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import ErrorBoundary from './ErrorBoundary';

interface UpcomingAppointmentsProps {
  unreadByBooking?: Record<string, number>;
  onUnreadCountChange?: (count: number) => void;
  onRefreshUnreadCounts?: () => void;
}

const UpcomingAppointments: React.FC<UpcomingAppointmentsProps> = ({
  unreadByBooking = {},
  onUnreadCountChange,
  onRefreshUnreadCounts
}) => {
  const { t } = useTranslation('dashboard');
  const { appointments, isLoading, refreshAppointments } = useAppointments();
  const [selectedAppointmentForRefund, setSelectedAppointmentForRefund] = useState<string | null>(null);

  // Calculate total unread count for upcoming appointments only
  const upcomingUnreadCount = useMemo(() => {
    const total = appointments.reduce((sum, appointment) => {
      return sum + (unreadByBooking[appointment.id] || 0);
    }, 0);

    console.log('UpcomingAppointments - calculated unread count:', total);
    return total;
  }, [appointments, unreadByBooking]);

  // Notify parent component when unread count changes
  useEffect(() => {
    if (onUnreadCountChange) {
      onUnreadCountChange(upcomingUnreadCount);
    }
  }, [upcomingUnreadCount, onUnreadCountChange]);

  const handleRefresh = () => {
    refreshAppointments();
  };

  console.log('UpcomingAppointments render - appointments:', appointments, 'isLoading:', isLoading, 'upcomingUnreadCount:', upcomingUnreadCount);

  return (
    <ErrorBoundary>
      <div>
      <div className="flex items-center justify-between mb-4 sm:mb-6 gap-2 min-w-0">
        <h2 className="text-xl sm:text-2xl font-medium truncate">Upcoming</h2>
        <Button 
          variant="outline" 
          size="icon" 
          onClick={handleRefresh} 
          title={t('refreshAppointments')}
          aria-label={t('refreshAppointments')}
          className="h-8 w-8 shrink-0"
        >
          <RefreshCcw className="h-4 w-4" />
        </Button>
      </div>
      
      <AppointmentList
        appointments={appointments}
        isLoading={isLoading}
        onRequestRefund={(id) => setSelectedAppointmentForRefund(id)}
        unreadByBooking={unreadByBooking}
        onRefreshUnreadCounts={onRefreshUnreadCounts}
      />
      
      {selectedAppointmentForRefund && (
        <RefundRequestModal
          isOpen={!!selectedAppointmentForRefund}
          onClose={() => setSelectedAppointmentForRefund(null)}
          bookingId={selectedAppointmentForRefund}
        />
      )}
      </div>
    </ErrorBoundary>
  );
};

export default UpcomingAppointments;
